// libraries
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { useCallback } from "react";
import { UserRound, Settings, LogOut, HelpCircle, Menu } from "lucide-react";
import HelpCentreModal from "../../modals/HelpCentreModal";
import { useDisclosure } from "../../hooks/useDisclosure";
import { useClickOutside } from "../../hooks/useClickOutside";

interface UserNavbarProps {
  onMenuClick?: () => void;
}

function UserNavbar({ onMenuClick }: UserNavbarProps) {
  const { logout, user, profileName } = useAuth();
  const dropdown = useDisclosure();
  const helpModal = useDisclosure();

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, [logout]);

  const dropdownRef = useClickOutside<HTMLDivElement>(dropdown.close);

  const userName = profileName || "User";
  const userEmail = user?.email || "";

  return (
    <>
      <nav className="!bg-[#1d365d]">
        <div className="w-full px-5 py-5 flex items-center justify-between relative">
          {/* Left — hamburger on mobile, logo on desktop */}
          <div className="flex items-center">
            {onMenuClick && (
              <button
                type="button"
                onClick={onMenuClick}
                className="lg:hidden p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
            )}
            <Link to="/home/dashboard" id="logo-nav" className="hidden lg:block">
              <img src="/DataRevival_REV_RGB.svg" alt="Data Revival" className="w-[180px] h-auto" />
            </Link>
          </div>

          {/* Center — logo on mobile only */}
          <Link to="/home/dashboard" className="lg:hidden absolute left-1/2 -translate-x-1/2">
            <img src="/DataRevival_REV_RGB.svg" alt="Data Revival" className="w-[140px] h-auto" />
          </Link>

          {/* Avatar + dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="flex rounded-full focus:outline-none focus:ring-2 focus:ring-white/30"
              onClick={dropdown.toggle}
              aria-expanded={dropdown.isOpen}
            >
              <span className="sr-only">Open user menu</span>
              <div className="w-8 h-8 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white text-sm font-semibold">
                {userName.charAt(0).toUpperCase()}
              </div>
            </button>

            {dropdown.isOpen && (
              <div className="absolute right-0 mt-2 z-50 w-48 rounded-lg bg-white border border-gray-200">
                <div className="px-3 py-2.5 border-b border-gray-100">
                  <span className="block text-sm font-medium text-gray-900 truncate">{userName}</span>
                  {userEmail && (
                    <span className="block text-xs text-gray-400 truncate mt-0.5">{userEmail}</span>
                  )}
                </div>
                <ul className="p-1">
                  <li>
                    <Link
                      to="/profile"
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <UserRound size={15} />
                      Profile
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <Settings size={15} />
                      Settings
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={helpModal.open}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <HelpCircle size={15} />
                      Help centre
                    </button>
                  </li>
                </ul>
                <div className="border-t border-gray-100 p-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <LogOut size={15} />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
      <HelpCentreModal isOpen={helpModal.isOpen} setIsOpen={helpModal.setIsOpen} />
    </>
  );
}

export default UserNavbar;
